<!--@format-->
<template>
    <div class="custom-insert-tag-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ widgetTitle }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else>
                    <div>
                        <app-input
                            v-model="customTag.name"
                            label="Label Text"
                            placeholder="Label text"
                            :error="errorMessage"
                            :is-valid="formInputValid"
                            @blur="$v.customTag.name.$touch()"
                        />
                    </div>
                    <div class="divider"></div>
                    <div class="custom-insert-tag-preview">
                        <p>Preview of Custom Insert Tag:</p>
                        <app-input
                            :label="customTag.name || 'Label Text'"
                            placeholder=" "
                            :disabled="true"
                        />
                    </div>
                    <div class="disclaimer">
                        <p>
                            Custom Insert Tags made within the Document Builder will solely exist in
                            the document in which they were created and any templated made from that
                            document.
                        </p>

                        <p>
                            To create Custom Insert Tags that are automatically generated for new
                            documents, navigate to <br />
                            <span> Account Settings > Customizations > Custom Insert Tags</span>.
                        </p>
                    </div>
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget()">Cancel</app-button>
                </div>
                <div class="action-container">
                    <app-button class="save-btn" @click="save()">Save</app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus';
import { startCase } from 'lodash';
import { required } from 'vuelidate/lib/validators';
import { CustomInsertTagsService } from '@/scripts/services/custom-tags/custom-insert-tags-service';
import { DocumentCustomTagsService } from '@/scripts/services/document/insert-tags/document-custom-tags-service';
import { DocumentInsertTagsService } from '@/scripts/services/document/insert-tags/document-insert-tags-service';
import { DocumentTemplateInsertTagsService } from '@/scripts/services/document/insert-tags/document-template-insert-tags-service';

const AppButton = () => import('../common/app-button.vue');
const AppInput = () => import('@/components/common/app-input.vue');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');

export default {
    name: 'custom-insert-tag-widget',

    components: {
        'loading-screen-svg': LoadingScreenSvg,
        'widget-close-svg': WidgetCloseSvg,
        'app-input': AppInput,
        'app-button': AppButton,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            customTag: {
                id: null,
                name: '',
            },

            errorMessage: 'This field is required',
        };
    },

    validations() {
        return {
            customTag: {
                name: {
                    required,
                },
            },
        };
    },

    computed: {
        accountId() {
            return this.widget.options.accountId;
        },

        documentId() {
            return this.widget.options.documentId;
        },

        documentTemplateId() {
            return this.widget.options.documentTemplateId;
        },

        isDocumentTemplate() {
            return this.$route.params.documentTemplateId !== undefined;
        },

        widgetTitle() {
            return this.widget.options.title;
        },

        customInsertTagId() {
            return this.widget.options.customInsertTagId;
        },

        isEditMode() {
            return (
                this.widget.options.customInsertTagId !== null &&
                this.widget.options.customInsertTagId !== undefined
            );
        },

        formInputValid() {
            return !(this.$v.customTag.name.$dirty && this.$v.customTag.name.$invalid);
        },
    },

    created() {
        if (this.isEditMode) {
            this.getCustomInsertTag();
        }
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        refreshCustomInsertTags() {
            EventBus.$emit('custom-insert-tags:update');
        },

        validateFields() {
            this.$v.customTag.name.$touch();
        },

        getCustomInsertTag() {
            this.isLoading = true;

            if (this.documentId) {
                DocumentInsertTagsService.get(this.customInsertTagId, this.documentId)
                    .then(({ data }) => {
                        this.customTag = {
                            id: data.value[0].Id,
                            name: data.value[0].CustomTagName,
                        };
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your custom insert tag', {
                            error,
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else if (this.documentTemplateId) {
                DocumentTemplateInsertTagsService.get(
                    this.customInsertTagId,
                    this.documentTemplateId,
                )
                    .then(({ data }) => {
                        this.customTag = {
                            id: data.value[0].Id,
                            name: data.value[0].CustomTagName,
                        };
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your custom insert tag', {
                            error,
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                CustomInsertTagsService.get(this.customInsertTagId)
                    .then(({ data }) => {
                        this.customTag = {
                            id: data.value[0].Id,
                            name: data.value[0].Name,
                        };
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification('Failed to get your custom insert tag', {
                            error,
                        });
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },

        save() {
            if (this.$v.customTag.$invalid) {
                this.validateFields();
                return;
            }

            if (this.isEditMode) {
                this.patchCustomTag();
            } else {
                this.createCustomTag();
            }
        },

        createCustomTag() {
            this.isLoading = true;

            const body = {
                Name: this.customTag.name,
                AccountId: this.accountId,
            };

            CustomInsertTagsService.post(body)
                .then(() => {
                    this.refreshCustomInsertTags();
                    this.closeWidget();
                    this.$helpers.showSuccessNotification(
                        'Your custom insert tag has been created',
                    );
                })
                .catch((error) => {
                    this.$helpers.showFailNotification('Failed to create your custom insert tag', {
                        error,
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },

        patchCustomTag() {
            this.isLoading = true;

            if (this.documentId || this.documentTemplateId) {
                const body = [
                    {
                        name: 'CustomTagName',
                        value: `${this.customTag.name}`,
                    },
                    {
                        name: 'CustomTagKey',
                        value: `[${startCase(this.customTag.name).replaceAll(' ', '')}]`,
                    },
                ];

                DocumentCustomTagsService.patch(this.customInsertTagId, body)
                    .then(() => {
                        EventBus.$emit('update-insert-tags');

                        this.closeWidget();
                        this.$helpers.showSuccessNotification(
                            'Your custom insert tag has been updated',
                        );
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while updating your custom tag.',
                            { error }
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                const body = {
                    name: this.customTag.name,
                };

                CustomInsertTagsService.put(this.customInsertTagId, body)
                    .then(() => {
                        this.refreshCustomInsertTags();
                        this.closeWidget();
                        this.$helpers.showSuccessNotification(
                            'Your custom insert tag has been updated',
                        );
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while updating your custom tag.',
                            { error }
                        );
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },
    },
};
</script>

<style>
.custom-insert-tag-widget .custom-insert-tag-preview {
    margin-bottom: 48px;
}

.custom-insert-tag-widget .custom-insert-tag-preview .app-input {
    background: #e9e9ef 0% 0% no-repeat padding-box;
    padding: 16px;
    border-radius: 11px;
    width: 296px;
}

.custom-insert-tag-widget .custom-insert-tag-preview .app-input label {
    font-size: 14px;
}

.custom-insert-tag-widget .custom-insert-tag-preview .app-input input {
    height: 32px;
}

.custom-insert-tag-widget .custom-insert-tag-preview p {
    font-weight: bold;
    margin-bottom: 16px;
}

.custom-insert-tag-widget .content-wrapper .divider {
    margin: 32px 0;
    width: 100%;
    border: 1px solid #cac6cb;
}

.custom-insert-tag-widget .content-wrapper .disclaimer p {
    color: #382153;
    font: 400 14px/21px 'Roboto';
    margin: 0;
}

.custom-insert-tag-widget .content-wrapper .disclaimer p span {
    color: #18aeff;
}

.custom-insert-tag-widget .content-wrapper .disclaimer p:first-child {
    margin-bottom: 18px;
}

.custom-insert-tag-widget .cancel-btn {
    width: 127px;
    background: transparent;
    color: #382153;
    border: 1px solid #cac6cb;
}

.custom-insert-tag-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.custom-insert-tag-widget .save-btn {
    width: 130px;
}
</style>
